import { SEO } from "../components/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO title="Privacy Policy" description="Privacy policy for Fit Print Bros. Learn how we handle your data." path="/privacy" />
      <div className="prose prose-lg max-w-none">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <p className="text-green-800 mb-2"><strong>Effective Date:</strong> January 1, 2024</p>
          <p className="text-green-700">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
        </div>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
          <h3 className="text-lg font-medium text-gray-800 mb-2">Personal Information:</h3>
          <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
            <li>Name, email address, phone number</li>
            <li>Billing and shipping addresses</li>
            <li>Payment information (processed securely through Stripe)</li>
          </ul>
          
          <h3 className="text-lg font-medium text-gray-800 mb-2">Order Information:</h3>
          <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
            <li>Product specifications and quantities</li>
            <li>Artwork files and design preferences</li>
            <li>Order history and communication records</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-800 mb-2">Technical Information:</h3>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>IP address, browser type, device information</li>
            <li>Website usage data and analytics</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>Process and fulfill your orders</li>
            <li>Communicate about your orders and our services</li>
            <li>Provide customer support</li>
            <li>Improve our website and services</li>
            <li>Send promotional emails (with your consent)</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Information Sharing</h2>
          <p className="text-gray-700 mb-4">We do not sell your personal information. We may share information with:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li><strong>Service Providers:</strong> Payment processors, shipping companies, email services</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>We use industry-standard security measures to protect your data</li>
            <li>Payment information is processed securely through Stripe</li>
            <li>Artwork files are stored securely and access is limited</li>
            <li>We regularly update our security practices</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Your Rights</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Update or correct your information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Portability:</strong> Request your data in a portable format</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Cookies & Tracking</h2>
          <p className="text-gray-700 mb-4">
            We use cookies and similar technologies to improve your experience, analyze website usage, 
            and provide personalized content. You can control cookie settings through your browser.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Third-Party Services</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li><strong>Google Analytics:</strong> Website analytics and performance tracking</li>
            <li><strong>Stripe:</strong> Secure payment processing</li>
            <li><strong>MailerSend:</strong> Email communications and notifications</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
          <p className="text-gray-700 mb-4">
            We retain your information for as long as necessary to provide services, comply with legal 
            obligations, and resolve disputes. Artwork files may be retained for reorder purposes.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
          <p className="text-gray-700 mb-4">
            Our services are not intended for children under 13. We do not knowingly collect 
            personal information from children under 13.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
          <p className="text-gray-700">
            For questions about this privacy policy or to exercise your rights, please contact us 
            through our website or email us directly.
          </p>
        </section>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-8">
          <p className="text-gray-700 text-sm">
            <strong>Last Updated:</strong> January 1, 2024<br />
            We may update this privacy policy from time to time. We will notify you of any 
            significant changes by posting the new policy on our website.
          </p>
        </div>
      </div>
    </div>
  );
};
// OMS wrapper that handles auth

export { PrivacyPolicy };
export default PrivacyPolicy;
